import { useRef, useState } from 'react';

import { FormError } from '@/shared/forms';

interface Props {
  name: string;
  /** Accessible name of the field. */
  label: string;
  className?: string;
  maxLength: number;
  onSave: (name: string) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Inline rename field (column and board titles). Enter saves, Escape cancels, and leaving the field (blur)
 * saves a changed non-empty name; an empty or unchanged one just closes the editor.
 */
export function InlineTitleEditor({ name, label, className, maxLength, onSave, onClose }: Props) {
  const [value, setValue] = useState(name);
  const [error, setError] = useState<unknown>(null);
  // Escape/Enter already decided the outcome: the blur they cause must not decide again.
  const settled = useRef(false);

  const commit = () => {
    if (settled.current) return;
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      settled.current = true;
      onClose();
      return;
    }
    settled.current = true;
    setError(null);
    onSave(trimmed).then(onClose, (cause: unknown) => {
      settled.current = false;
      setError(cause);
    });
  };

  return (
    <>
      <input
        className={className}
        aria-label={label}
        value={value}
        maxLength={maxLength}
        autoFocus
        onFocus={(event) => {
          event.currentTarget.select();
        }}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            settled.current = true;
            onClose();
          }
        }}
      />
      <FormError error={error} />
    </>
  );
}
